import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocalStorage } from "usehooks-ts";

interface AppLayoutContextValue {
  isFullScreenPage: boolean;
  setIsFullScreenPage: Dispatch<SetStateAction<boolean>>;
  isNavExpanded: boolean;
  setIsNavExpanded: Dispatch<SetStateAction<boolean>>;
}

const AppLayoutContext = createContext<AppLayoutContextValue | null>(null);

export const AppLayoutContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isFullScreenPage, setIsFullScreenPage] = useState(false);
  const [isNavExpanded, setIsNavExpanded] = useLocalStorage(
    "isNavExpanded",
    true,
  );

  return (
    <AppLayoutContext.Provider
      value={{
        isFullScreenPage,
        setIsFullScreenPage,
        isNavExpanded,
        setIsNavExpanded,
      }}
    >
      {children}
    </AppLayoutContext.Provider>
  );
};

export const useLayoutContext = () => {
  const context = useContext(AppLayoutContext);

  if (!context) {
    throw new Error(
      "useLayoutContext must be used within a LayoutContextProvider",
    );
  }

  return context;
};

/**
 * Hook to use on the root component of a page that should take up the entire
 * screen width.
 *
 * Using this hook will hide the floating buttons, adjust root content styles
 * and then show them again when unmounting.
 */
export const useFullScreenPageLayout = () => {
  const { setIsFullScreenPage } = useLayoutContext();

  useEffect(() => {
    setIsFullScreenPage(true);

    return () => {
      setIsFullScreenPage(false);
    };
  }, [setIsFullScreenPage]);
};
